import { FC } from 'react';
import moment from 'moment';
import IconButton from '@mui/material/IconButton';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { useNavigate } from 'react-router-dom';
import { ScheduleItemStatus } from '../../billingTypes';
import { useShowContext } from 'framework/show';
import {
  ACCESS_INFO_SECTIONS,
  ACCESS_INFO_ACTIONS,
  useProjectSecurityContext,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import { Button } from 'framework/ui/mui-builder';
import { DATE_FORMAT, openPageInNewTab } from 'framework/utils';
import { isMigrateEnabled } from '../../billingUtils';

interface MilestoneRowData {
  id: string;
  type: 'MILESTONE' | 'CHANGE_REQUEST';
  status: ScheduleItemStatus;
  billingScheduleId: string;
  billingRequestId: string | null;
  hasLinkedPendingCR: boolean;
  isLinkedCR: boolean;
  isCompleted: boolean;
  invoiceDate: string | null;
}

const isInvoiceDateBeforeThisMonth = (invoiceDate: string | null): boolean => {
  if (!invoiceDate) return false;
  const date = moment(invoiceDate, DATE_FORMAT.API_ONLY_DATE);
  const startOfThisMonth = moment().startOf('month');
  return date.isValid() && date.isBefore(startOfThisMonth);
};

interface MilestoneActionFieldProps {
  id: string;
}

const MilestoneActionField: FC<MilestoneActionFieldProps> = ({ id: projectId }) => {
  const { data } = useShowContext<MilestoneRowData>();
  const rowData = data.getData() as MilestoneRowData;

  const {
    getCreateBillingRequestUrl,
    getUpdateBillingRequestUrl,
    getMigrateBillingRequestUrl,
    getUpdateMigratedBillingRequestUrl,
  } = useNewProjectUrl();
  const navigate = useNavigate();
  const { isAccessGranted } = useProjectSecurityContext();

  const {
    id: billingItemId,
    type,
    status: currentStatus,
    billingScheduleId,
    billingRequestId,
    hasLinkedPendingCR,
    isLinkedCR,
    isCompleted,
    invoiceDate,
  } = rowData;

  const hasManagePermission = isAccessGranted(ACCESS_INFO_SECTIONS.BILLING_SCHEDULE, ACCESS_INFO_ACTIONS.MANAGE);

  const canMigrate = isMigrateEnabled && isInvoiceDateBeforeThisMonth(invoiceDate) && !billingRequestId;
  const canEditMigrated =
    isMigrateEnabled &&
    isInvoiceDateBeforeThisMonth(invoiceDate) &&
    Boolean(billingRequestId) &&
    currentStatus === ScheduleItemStatus.APPROVED;

  const isChangeRequest = type === 'CHANGE_REQUEST';
  const canInitiate = currentStatus === ScheduleItemStatus.PLANNED && (isChangeRequest || isCompleted);
  const canUpdate =
    (currentStatus === ScheduleItemStatus.DRAFT || currentStatus === ScheduleItemStatus.CLARIFICATION_REQUESTED) &&
    Boolean(billingRequestId);
  const shouldHide = isLinkedCR || hasLinkedPendingCR || !hasManagePermission;

  const handleInitiate = () => {
    openPageInNewTab(getCreateBillingRequestUrl({ billingScheduleId, billingItemId }));
  };

  const handleUpdate = () => {
    openPageInNewTab(
      getUpdateBillingRequestUrl({
        billingScheduleId,
        billingRequestId: billingRequestId!,
      }),
    );
  };

  const handleMigrate = () => {
    navigate(getMigrateBillingRequestUrl({ billingScheduleId, billingItemId }));
  };

  const handleEditMigrated = () => {
    openPageInNewTab(
      getUpdateMigratedBillingRequestUrl({
        billingScheduleId,
        billingRequestId: billingRequestId!,
      }),
    );
  };

  if (shouldHide) {
    return null;
  }

  if (canMigrate) {
    return <Button onClick={handleMigrate}>Migrate</Button>;
  }

  if (canEditMigrated) {
    return (
      <IconButton onClick={handleEditMigrated} data-testid="edit-migrated-button">
        <AppRegistrationIcon color="primary" />
      </IconButton>
    );
  }

  if (canUpdate) {
    return (
      <IconButton onClick={handleUpdate} data-testid="update-button">
        <AppRegistrationIcon color="primary" />
      </IconButton>
    );
  }

  if (canInitiate) {
    return <Button onClick={handleInitiate}>Initiate</Button>;
  }

  return null;
};

export default MilestoneActionField;
